import React, { FC, useEffect } from 'react';

import { Box, Grid } from '@mui/material';

import {
  getTypeOfUser,
  PharmacistLegendVariant,
  PharmacyLegendVariant,
  TypeOfUser,
  WorkshiftStatus,
} from '@pharmaplan/common';

import { nameTypes } from '../SelfService/Workshifts/WorkshiftForm/ViewEditContract/ViewEditContractTime/ViewEditContractTime';
import DateBox from '../SelfService/Workshifts/WorkshiftForm/ViewEditContract/ViewEditContractTime/DateBox';
import useViewAllLongTermContracts from '../../hooks/ViewAllLongTermContracts/useViewAllLongTermContracts';
import useLongTermFormikDetails from '../../hooks/LongTermContract/useLongTermFormikDetails';
import LongTermModalLayout from '../common/LongTermModalLayout';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import LongContractCard from '../common/LongContractCard';
import { resetDialog } from '../../reducers/dialogReducer';
import { getDayMonthDateYear } from '../Reports/helpers';
import CustomChip from '../common/CustomChip';
import strings from '../../localization';

import styles from './styles';
import { serverToday } from '../../helpers/Functions';
import { ChipType } from '../../helpers/Constants';
import { useAppSelector } from '../../hooks/useAppSelector';

const { start, end } = nameTypes;
interface IViewAllLongTermContracts {
  longTermContractId: string;
  type: PharmacistLegendVariant | PharmacyLegendVariant;
  professionalId?: string;
}

const ViewAllLongTermContracts: FC<IViewAllLongTermContracts> = ({
  longTermContractId,
  professionalId,
  type,
}) => {
  const dispatch = useAppDispatch();

  const userType = useAppSelector(getTypeOfUser);
  const isPharmacy = userType === TypeOfUser.pharmacy;

  const isBooked = type === PharmacistLegendVariant.booked;

  const {
    getAPI,
    contract,
    userData,
    longTermContract,
    successAction,
    status,
  } = useViewAllLongTermContracts({
    longTermContractId,
    type,
    professionalId,
  });

  const { data, proposedAllowance } = contract ?? {};

  const { formik } = useLongTermFormikDetails(
    data,
    longTermContract,
    isBooked ? '' : proposedAllowance,
  );

  useEffect(() => {
    getAPI();
  }, []);

  const handleClose = () => {
    dispatch(resetDialog());
  };

  const chipLabel = {
    [PharmacistLegendVariant.requested]: strings.requested,
    [PharmacistLegendVariant.booked]: strings.booked,
    [PharmacistLegendVariant.workshift]: strings.posted,
    [PharmacyLegendVariant.posted]: strings.posted,
  };

  const statusConfig = {
    ...status,
    [WorkshiftStatus.Requested]: {
      type: PharmacistLegendVariant.requested,
      label: strings.requested,
    },
    [WorkshiftStatus.Posted]: {
      type: PharmacistLegendVariant.workshift,
      label: strings.posted,
    },
  };

  return (
    <LongTermModalLayout
      primary={{ label: strings.close, onPress: handleClose }}
    >
      <Grid md={5} xs={12} item>
        <LongContractCard
          formik={formik}
          event={userData}
          successAction={successAction}
        />
      </Grid>
      <Grid md={7} xs={12} item>
        <Box sx={styles.dateBoxGrid}>
          {data?.map?.((interval) => {
            let Chip;
            const { startDate } = interval ?? {};
            const date = startDate?.split?.('T')?.[0];

            const showUnfulfilled = startDate
                < (serverToday()?.toISO({ includeOffset: false }) ?? '')
              && type === PharmacistLegendVariant.workshift;

            if (showUnfulfilled) {
              Chip = (
                <CustomChip
                  type={ChipType.notAvailable}
                  label={strings.unfulfilled}
                />
              );
            } else if (type === PharmacistLegendVariant.workshift) {
              const { type: chipType, label } = statusConfig[interval?.status as keyof typeof statusConfig]
                ?? {};

              Chip = <CustomChip type={chipType} label={label} />;
            } else {
              Chip = (
                <CustomChip
                  type={type}
                  label={chipLabel[type as keyof typeof chipLabel]}
                />
              );
            }

            return (
              <DateBox
                Chip={Chip}
                key={date}
                showCheckbox={false}
                changedName={`${date}|${nameTypes.changed}`}
                title={`${getDayMonthDateYear(date)}`}
                startName={`${date}|${start}`}
                endName={`${date}|${end}`}
                formik={formik}
                hideDayOff
                disabled
                dayOffName=""
              />
            );
          })}
        </Box>
      </Grid>
    </LongTermModalLayout>
  );
};

export default ViewAllLongTermContracts;
